import { useEffect, useState } from "react";
import { Camera, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, compressImage, errMsg, imgSrc, queue } from "@/lib/api";
import { Btn, Pick, useStageNames } from "./bits";

export function PhotoStrip({ photos, onChange, disabled, max = 6, testid = "photo" }) {
  const add = async (e) => {
    const files = [...e.target.files].slice(0, max - photos.length);
    const urls = await Promise.all(files.map((f) => compressImage(f)));
    onChange([...photos, ...urls]);
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testid}-strip`}>
      {photos.map((p, i) => (
        <div key={i} className="relative w-20 h-20 border border-[#334155]">
          <a href={imgSrc(p)} target="_blank" rel="noreferrer"><img src={imgSrc(p)} alt={`Evidence ${i + 1}`} className="w-full h-full object-cover" /></a>
          {!disabled && <button type="button" onClick={() => onChange(photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-red-500 p-0.5" aria-label="Remove photo"><X className="w-3 h-3" /></button>}
        </div>
      ))}
      {!disabled && photos.length < max && (
        <label className="w-20 h-20 border border-dashed border-[#334155] flex flex-col items-center justify-center text-slate-400 hover:border-amber-500 cursor-pointer text-[10px] gap-1" data-testid={`${testid}-add`}>
          <Camera className="w-5 h-5" />Add photo
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={add} />
        </label>
      )}
    </div>
  );
}

export function PunchDialog({ open, setOpen, projectId, tag, defaultDescription = "", defaultStage = "B", onSaved }) {
  const sn = useStageNames();
  const blank = { tag_id: tag?.id || "", description: defaultDescription, category: "A", stage: defaultStage, owner: "", due_date: "", photos: [] };
  const [f, setF] = useState(blank);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setF(blank); setSearch(""); } }, [open]);
  useEffect(() => {
    if (tag || search.length < 2) {
      setTags([]);
      return undefined;
    }
    const t = setTimeout(() => api.get(`/projects/${projectId}/tags`, { params: { search, limit: 8 } }).then((r) => setTags(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search, tag, projectId]);

  const save = async () => {
    const url = `/projects/${projectId}/punch`;
    try {
      const r = await api.post(url, f);
      toast.success(`Punch ${r.data.number} raised`);
      onSaved?.(r.data);
    } catch (e) {
      if (e.response) return toast.error(errMsg(e));
      queue.add({ id: `pun-${Date.now()}`, method: "post", url, body: f, label: "Punch item" });
      toast.success("Saved offline. It will sync when you reconnect.");
    }
    setOpen(false);
  };
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0F172A] border-[#1E2D4A] max-h-[90vh] overflow-y-auto" data-testid="punch-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Raise punch item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {tag ? <p className="num text-sm text-amber-400">Tag {tag.tag_no}</p> : (
            <div>
              <Input placeholder="Search tag number" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="punch-tag-search" />
              <div className="mt-1 max-h-40 overflow-y-auto">
                {tags.map((t) => (
                  <button key={t.id} type="button" onClick={() => { set("tag_id")(t.id); setSearch(t.tag_no); setTags([]); }} data-testid={`punch-tag-option-${t.tag_no}`}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-[#131C31] ${f.tag_id === t.id ? "text-amber-400" : "text-slate-300"}`}><span className="num">{t.tag_no}</span> · {t.description}</button>
                ))}
              </div>
            </div>
          )}
          <Textarea placeholder="Description" value={f.description} onChange={set("description")} data-testid="punch-description" />
          <div className="grid grid-cols-2 gap-2">
            <Pick testid="punch-category" value={f.category} onChange={set("category")} options={[["A", "Cat A · blocks milestone"], ["B", "Cat B · close after"]]} />
            <Pick testid="punch-stage" value={f.stage} onChange={set("stage")} options={["A", "B", "C"].map((s) => [s, `Raised at ${s} · ${sn[s]}`])} />
            <Input placeholder="Action by" value={f.owner} onChange={set("owner")} data-testid="punch-owner" />
            <Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="punch-due-date" />
          </div>
          <PhotoStrip photos={f.photos} onChange={set("photos")} testid="punch-photo" />
          <Btn onClick={save} disabled={!f.tag_id || !f.description} className="w-full" data-testid="punch-save">Raise punch</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
